/** The `values` area: the words of the values kit (src/components/values/),
 *  one renderer per data type (docs/DESIGN_SYSTEM.md "Values and entries").
 *  Each `*.words*` key is what a screen reader hears for a value, a sentence
 *  whose slots are what the eye reads; the words outside the slots say what
 *  the signs say. Owned by the slot components-a (docs/I18N.md §11). */
export const values = {
  /** A person: the name, as the eye reads it. */
  'values.person.words': '{name}',
  /** A group: the square avatar says it is a group. */
  'values.group.words': 'Group {name}',
  /** An item: its glyph says the health; a closed item recedes. */
  'values.item.words.plain': '{title}',
  'values.item.words.warning': '{title}, warning',
  'values.item.words.critical': '{title}, critical',
  'values.item.words.done': '{title}, Done',
  'values.item.words.cancelled': '{title}, Cancelled',
  /** An item the viewer cannot see: never a title, a date or a name. */
  'values.item.hidden': 'Not visible',
  'values.item.hiddenWords.plain': '{hidden}',
  'values.item.hiddenWords.cancelled': '{hidden}, Cancelled',
  /** An item the viewer sees only as its stub: its name and the lock. */
  'values.item.stubWords.plain': '{title}, no access',
  'values.item.stubWords.cancelled': '{title}, no access, Cancelled',
  'values.item.stubWords.done': '{title}, no access, Done',
  'values.item.stubWords.endsAfter': "{title}, no access, ends after this item's end",
  /** A date value; past due, the sign takes a badge. */
  'values.date.words': '{date}',
  'values.date.pastDue': '{date}, past due',
  /** The coarser grains, named by their own token; the year only when it is
   *  not the current one. */
  'values.date.week': 'W{week}',
  'values.date.weekOfYear': 'W{week} {year}',
  'values.date.quarter': 'Q{quarter}',
  'values.date.quarterOfYear': 'Q{quarter} {year}',
  /** A value that spans two dates. */
  'values.date.range': '{from} – {to}',
  /** A stage: the flow's own word, after the stage sign. */
  'values.stage.words': 'Stage {stage}',
  /** A tag: the path, after the faint hash sign. */
  'values.tag.words': 'Tag {path}',
  /** A health state: the pill's tone and dot shape carry the grade. */
  'values.health.words.critical': '{state}, critical',
  'values.health.words.warning': '{state}, warning',
  'values.health.words.notice': '{state}, notice',
  'values.health.words.info': '{state}',
  /** The platform's health states (`HEALTH_STATES`, src/domain/tags.ts): one
   *  word each, the same in every org (ruled 2026-10-14). */
  'values.health.state.disputed': 'Denied',
  'values.health.state.blocked': 'Blocked',
  'values.health.state.stale': 'Stalled',
  'values.health.state.unrequested': 'No requester',
  'values.health.state.watch': 'Watch',
  'values.health.state.atRisk': 'At risk',
  'values.health.state.delayed': 'Delayed',
  'values.health.state.blockedUpstream': 'Blocked upstream',
  'values.health.state.offTrack': 'Off track',
  /** A lifecycle state: the chip's own word, the verdict's axis ahead of it. */
  'values.lifecycle.words': '{state}',
  'values.lifecycle.withAxis': '{axis} {state}',
  /** The glyph's tip (glyph-tip.tsx): where the work stands, then its health. */
  'values.glyph.active': 'Active: marked as in progress.',
  'values.glyph.idle': 'Idle: nobody is working on it right now.',
  'values.glyph.review': 'Review: the work is done and waits on its review.',
  'values.glyph.done': 'Done: the work is finished.',
  'values.glyph.cancelled': 'Cancelled: ended without the work being done.',
  'values.glyph.clean': 'Nothing flagged.',
  'values.glyph.flagged': 'Flagged: a state needs a look.',
  'values.glyph.critical': 'Critical: a state needs attention now.',
  /** A number, in tabular figures. */
  'values.number.words': '{value}',
  /** Text a person typed, in typographic quotes. */
  'values.quoted': '“{text}”',
  /** A change of one value: the old struck, an arrow, the new. */
  'values.change.changed': 'from {from} to {to}',
  'values.change.added': 'added {value}',
  'values.change.removed': 'removed {value}',
  /** The "not affected" stance this item holds toward a related one: the sign
   *  and the avatar of whoever took it. */
  'values.stance.contain': '{name}: not affected',
} as const;
